import { merge } from 'lodash-es';
import { defineStore } from 'pinia';
import { h } from 'vue';
import type { ErrorMessageMode, Nullable } from '@mts-fe/types';
import { $MTS, message, useConfirm } from '@mts-fe/utils';
import {
  PROJ_INFO_KEY,
  SUB_TOKEN_KEY,
  TENANT_KEY,
  TOKEN_KEY,
  USER_INFO_KEY,
} from '@mts/enums/cacheEnum';
import { PageEnum } from '@mts/enums/pageEnum';
import { t } from '@mts/hooks/web/useI18n';
import { useWatermark, type UseWatermarkRes } from '@mts/hooks/web/useWatermark';
import { openLocaleWorker } from '@mts/locales/helper';
import { openDictMapWorker } from '@mts/render/api/worker';
import { getAuthCache, setAuthCache } from '@mts/utils/auth';
import { globSetting, IS_IMS, IS_LOWCODE } from '@mts/utils/env';
import { registeUserAppEvents } from '@mts/adapter';
import { createApi } from '@/api';
import API, { fetchUserInfo } from '@/api/ims-auth';
import type {
  LoginPayload,
  LoginResponse,
  MenuInfo,
  PlatformTenantInfo,
  ProjectInfo,
  TenantProjectInfo,
  UserAggregateInfo,
  UserInfo,
} from '@/api/ims-auth/model';
import { router } from '@/router';
import { getErrorLogStore, getGlobalStore, getLocaleStore, getPermissionStore } from '@/store';

interface UserState {
  userInfo: Nullable<UserInfo>;
  token?: string;
  subToken?: string;
  menuList: MenuInfo[];
  tenantList: PlatformTenantInfo[];
  currentTenant: string;
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  projectInfo: ProjectInfo;
}
let watermark: UseWatermarkRes;

export const useUserStore = defineStore('app-user', {
  state: (): UserState => ({
    // user info
    userInfo: null,
    // token
    token: undefined,
    subToken: undefined,
    // menuList
    menuList: [],
    // tenantList
    tenantList: [],
    currentTenant: '',
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    projectInfo: getAuthCache<ProjectInfo>(PROJ_INFO_KEY) || {
      platformTitle: globSetting.title,
      platformLogo: globSetting.logo,
      loginLogo: '/login-logo.png',
      loginBg: '/login-bg.png',
      loginCopyRight: '/login-copyright.png',
    },
  }),
  getters: {
    getUserInfo(state): UserInfo {
      const userInfo = state.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
      if (!userInfo.deptLink) return userInfo;

      const { group, workshop, section, factory } = userInfo.deptLink;
      userInfo.groupId = group?.id;
      userInfo.workshopId = workshop?.id;
      userInfo.sectionId = section?.id;
      userInfo.factoryId = factory?.id;
      return userInfo;
    },
    getUserHeader(): Recordable<string> {
      const { id = '', deptIds, deptNames, roleIds, roleNames } = this.getUserInfo;
      return {
        'metas-user-id': id,
        'metas-dept-ids': deptIds?.toString(),
        'metas-dept-names': encodeURIComponent(deptNames?.toString()),
        'metas-role-ids': roleIds?.toString(),
        'metas-role-names': encodeURIComponent(roleNames?.toString()),
      };
    },
    getToken(state): string {
      return state.token || getAuthCache<string>(TOKEN_KEY) || '';
    },
    getSubToken(state): string {
      return state.subToken || getAuthCache<string>(SUB_TOKEN_KEY) || '';
    },
    getMenuList(state): MenuInfo[] {
      return state.menuList || [];
    },
    getTenantList(state): PlatformTenantInfo[] {
      return state.tenantList || [];
    },
    getCurrentTenant(state): string {
      return state.currentTenant || getAuthCache<string>(TENANT_KEY) || (IS_LOWCODE ? '2660' : '1');
    },
    getSessionTimeout(state): boolean {
      return !!state.sessionTimeout;
    },
    getLastUpdateTime(state): number {
      return state.lastUpdateTime;
    },
    getProjectInfo(state) {
      return state.projectInfo;
    },
  },
  actions: {
    setToken(info?: string) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, this.token);
    },
    setSubToken(info?: string) {
      this.subToken = info ? info : ''; // for null or undefined value
      setAuthCache(SUB_TOKEN_KEY, this.subToken);
    },

    setUserInfo(info: UserInfo | null) {
      info && !info.homePath && (info.homePath = PageEnum.BASE_HOME);
      this.userInfo = info;
      this.lastUpdateTime = Date.now();
      setAuthCache(USER_INFO_KEY, info);
    },
    setMenuList(menuList: MenuInfo[]) {
      this.menuList = menuList;
    },
    setTenantList(tenantList: PlatformTenantInfo[] = []) {
      this.tenantList = tenantList;
    },
    async setCurrentTenant(tenantId: string, doLogin?: boolean) {
      this.currentTenant = tenantId;
      setAuthCache(TENANT_KEY, tenantId);

      const params = new URLSearchParams(location.hash.split('?')[1] || '');
      const mtsT = params.get('mts_t');
      if (mtsT && mtsT !== tenantId) message.warn('请检查租户是否正确~');

      const tenant = this.tenantList.find((i) => i.tenantId === tenantId);
      if (!tenant) return;

      this.setToken(tenant.token);
      if (!doLogin) return;

      try {
        const res = await API.loginIms({
          username: tenant.imsUsername,
          password: tenant.imsPassword,
        });
        this.setSubToken(res.token);
      } catch (error) {
        message.error({ content: '业务系统登录失败：' + error.message });
      }
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    setProjectInfo(info?: TenantProjectInfo) {
      if (!info) return;

      info.metasLangList?.length &&
        getLocaleStore().setLocaleInfo({ availableLocales: info.metasLangList });

      this.projectInfo = merge(this.projectInfo, {
        platformTitle: info.extData1,
        platformLogo: info.extData2 ?? globSetting.logo,
        loginLogo: info.extData5,
        loginBg: info.extData4 ?? '/login-bg.png',
        loginCopyRight: info.extData3,
        projectConfig: info.extDataJson,
        projectTitle: info.name,
        shortTitle: info.nameShort,
        projectLogo: info.logoUri,
      });
      setAuthCache(PROJ_INFO_KEY, this.projectInfo);
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.subToken = '';
      this.sessionTimeout = false;
      this.menuList = [];
      this.tenantList = [];
      this.currentTenant = '';
    },
    /**
     * @description: login
     */
    async login(
      params: LoginPayload & { goHome?: boolean; mode?: ErrorMessageMode },
    ): Promise<LoginResponse | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await API.login(loginParams, mode);

        // save token
        this.setToken(data.token);

        openLocaleWorker();
        goHome && (await router.replace(data.homePath || PageEnum.BASE_HOME));
        return data;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    /**
     * @description: login by card or face
     */
    async loginByCard(params: {
      cardCode?: number;
      no?: string;
      isCard?: boolean;
      goHome?: boolean;
    }): Promise<LoginResponse | null> {
      try {
        if (this.getToken) await this.logout(false);

        const { goHome = true, isCard = true, ...loginParams } = params;

        // 构建设备信息
        const loginDeviceInfo = {
          deviceId: $MTS.App.prop($MTS.PROP_DEVICE_ID),
          devicePlatform: $MTS.App.prop($MTS.PROP_PLATFORM),
          deviceOsVersion: $MTS.App.prop($MTS.PROP_OS_VER),
          deviceShellVersion: $MTS.App.prop($MTS.PROP_SHELL_VER),
          deviceRam: $MTS.App.prop($MTS.PROP_RAM),
        };

        let data;
        if (isCard) {
          // 刷卡登陆
          data = await createApi('/ims-auth/auth/loginByPadAndCard')({
            ...loginParams,
            loginDeviceInfo,
          });
        } else {
          // 人脸登陆
          data = await createApi('/ims-auth/auth/loginByFace')({
            ...loginParams,
            loginDeviceInfo,
            userId: loginParams?.no,
          });
        }

        $MTS.App.stopCamLogin();

        // save token
        this.setToken(data.token);
        localStorage.setItem('mts-username', data.username);
        localStorage.removeItem('mts-login-info');

        goHome && (await router.replace(data.homePath || PageEnum.BASE_HOME));
        // 非登录页刷卡登录重新加载
        !router.currentRoute.value.fullPath.includes('/login') &&
          getGlobalStore().forceReloadView();
        return data;
      } catch (error) {
        return Promise.reject(error);
      }
    },

    async getUserInfoAction(): Promise<UserAggregateInfo | null> {
      if (!this.getToken) return null;

      openDictMapWorker();
      const data = await this.loadUserInfo();

      IS_IMS && (await getGlobalStore().initGlobalConfig());
      registeUserAppEvents();

      requestIdleCallback(() => this.updateWatermark());
      return data;
    },
    async loadUserInfo(): Promise<UserAggregateInfo> {
      const data = await fetchUserInfo();

      this.setUserInfo(data.userInfo);
      this.setMenuList(data.menuList);
      this.setProjectInfo(data.tenantInfo);
      data.tenantInfoList?.length && this.setTenantList(data.tenantInfoList);
      const currentTenant =
        data.tenantInfoList?.length !== 1 ? this.getCurrentTenant : data.tenantInfoList[0].tenantId;
      await this.setCurrentTenant(currentTenant, true);
      getPermissionStore().setPermCodeList(data.buttonPermissionList);
      return data;
    },
    updateWatermark() {
      if (!watermark) {
        watermark = useWatermark();
      }

      const { nickname, no } = this.getUserInfo;
      if (!nickname) {
        watermark.clearAll();
        return;
      }

      const projectTitle = this.getProjectInfo.shortTitle;
      watermark.setWatermark([projectTitle, nickname, no].filter(Boolean).join(' | '));
    },
    /**
     * @description: logout
     */
    async logout(goLogin = true) {
      if (this.getToken) {
        try {
          await API.logout();
        } catch {
          console.error('注销Token失败');
        }
      }
      console.debug('[LOGOUT]', getErrorLogStore().getErrorLogInfoList);
      this.setToken(undefined);
      this.setSubToken(undefined);
      this.setSessionTimeout(false);
      this.setUserInfo(null);
      this.updateWatermark();
      getGlobalStore().clearLocalCache();
      const { fullPath } = router.currentRoute.value;
      if (goLogin || fullPath === PageEnum.BASE_HOME) {
        // 直接回登陆页
        router.replace(PageEnum.BASE_LOGIN);
      } else {
        // 回登陆页带上当前路由地址
        router.replace({
          path: PageEnum.BASE_LOGIN,
          query: {
            redirect: encodeURIComponent(fullPath),
          },
        });
      }
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      useConfirm({
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          // 主动登出，不带redirect地址
          await this.logout(true);
        },
      });
    },
  },
});
